import React from 'react';
import { FetchResult } from '@apollo/client';

import Heading from '@buyside/components/presentation/ContributorDashboard/contributorDashboardHeading';
import Link from '@buyside/components/presentation/Link/link';
import LoadingSpinner, {
  SIZES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';
import PostIndexCard from '@buyside/components/presentation/PostIndex/postIndexCard';
import StatusUpdateForm from '@buyside/components/presentation/StatusUpdateForm/statusUpdateForm';

const CurrentUserPublishedPosts = require('buyside/graphql/queries/CurrentUserPublishedPosts');

import {
  CreatePostMutationVariables,
  CreatePostMutation,
  CurrentUserPublishedPostsQueryVariables,
  CurrentUserPublishedPostsQuery,
  useCurrentUserPublishedPostsQuery,
  useCreatePostMutation
} from '@interfaces/graphql-typings';

import { postsUrl } from '@buyside/util/urlHelper';

import styles from '@buyside/components/presentation/ContributorDashboard/contributorDashboardStyles.scss';

export const postHeading = 'Insights';

const MyPosts: React.FC = () => {
  const { error, data, loading } = useCurrentUserPublishedPostsQuery();

  const [createPost, { error: mutationError }] = useCreatePostMutation({
    update(cache, { data }) {
      const newPost = data?.createPost?.post;
      if (!newPost) return;

      const cachedQuery = cache.readQuery<
        CurrentUserPublishedPostsQuery,
        CurrentUserPublishedPostsQueryVariables
      >({
        query: CurrentUserPublishedPosts
      });

      if (cachedQuery?.currentUser && newPost) {
        cache.writeQuery({
          query: CurrentUserPublishedPosts,
          data: {
            currentUser: {
              ...cachedQuery.currentUser,
              posts: [newPost, ...(cachedQuery.currentUser?.posts || [])]
            }
          }
        });
      }
    }
  });

  if (loading)
    return (
      <>
        <Heading title={postHeading} />
        <LoadingSpinner size={SIZES.LARGE} />
      </>
    );
  if (error) throw error;
  if (!data || !data.currentUser) return null;

  const { acceptedPostTerms, isAdmin, posts, profileImageUrl } = data.currentUser;
  const termsAccepted = Boolean(acceptedPostTerms);

  return (
    <>
      <Heading title={postHeading} />
      <StatusUpdateForm
        profileImageUrl={profileImageUrl}
        onSubmit={(values: CreatePostMutationVariables): Promise<FetchResult<CreatePostMutation>> =>
          createPost({ variables: values })
        }
        termsAccepted={termsAccepted}
        isAdmin={isAdmin}
      />
      <div className={styles.error}>{mutationError?.graphQLErrors[0]?.message}</div>
      {posts?.length ? (
        <>
          {posts.map(post => {
            if (!post) return null;
            const {
              id,
              body,
              commentsCount,
              currentUserHasLiked,
              eventId,
              domain,
              likeCount,
              ogDescription,
              ogUrl,
              ogPreviewImageUrl,
              ogTitle,
              postedAt,
              tagList,
              untaggedBody,
              user,
              youtubeId
            } = post;
            return (
              <PostIndexCard
                key={id}
                body={body}
                commentsCount={commentsCount}
                currentUserHasLiked={currentUserHasLiked}
                eventId={eventId}
                domain={domain}
                id={id}
                isAuthor
                isIndex={false}
                likeCount={likeCount}
                ogDescription={ogDescription}
                ogUrl={ogUrl}
                ogPreviewImageUrl={ogPreviewImageUrl}
                ogTitle={ogTitle}
                postedAt={postedAt}
                tagList={tagList}
                showUserDetails
                untaggedBody={untaggedBody}
                user={user}
                youtubeId={youtubeId}
              />
            );
          })}
        </>
      ) : (
        <div className={styles.noContentMsg}>
          <p>
            SumZero Insights was built to allow you to more quickly share notes/brief
            thoughts/articles, or even ask the community if someone would like to trade notes on a
            given name or sector. Insights are meant to allow for more frequent interactions with
            the broader SumZero community, and to help our community build their individual network.
          </p>
          <p>
            <strong>Important</strong>: Be sure to cashtag (e.g., $APPS, $ERII, $ABG) related
            tickers so that those following the tagged entities or the relevant industry are alerted
            to your post and can engage with you.
          </p>
          <p>
            <Link to={postsUrl()}>Explore All Insights</Link>
          </p>
          <p>
            Contact your rep or{' '}
            <strong>
              <a href="mailto:access@sumzero.com">access@sumzero.com</a>
            </strong>{' '}
            to discuss how others are leveraging this tool and how we might recommend it for you.
          </p>
        </div>
      )}
    </>
  );
};

export default MyPosts;
